import React, { useState, useEffect } from 'react';
import '../styles/Login.css';
import kmfLogo from '../assets/obraswise-logo.png';
// Remover importações do Firebase Auth
// import { auth } from '../firebase/config';
// import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { apiService } from '../services/ApiService';
import { useTheme } from '../contexts/ThemeContext';
import { FiSun, FiMoon, FiEye, FiEyeOff } from 'react-icons/fi';

// Lista de domínios de email públicos
const dominiosPublicos = [
  '@gmail.com', '@outlook.com', '@hotmail.com', '@live.com', '@msn.com',
  '@yahoo.com', '@yahoo.com.br', '@icloud.com', '@me.com', '@mac.com',
  '@zoho.com', '@protonmail.com', '@tutanota.com', '@uol.com.br',
  '@bol.com.br', '@terra.com.br', '@ig.com.br', '@globo.com', '@r7.com',
  '@oi.com.br', '@gmx.com', '@mail.com', '@aol.com', '@yandex.com',
  '@fastmail.com', '@startmail.com', '@mailbox.org', '@posteo.de',
  '@runbox.com', '@criptext.com', '@example.com'
];

// Função para extrair o domínio do email
function extrairDominio(email) {
  if (!email) return null;
  const indexAt = email.indexOf('@');
  return indexAt !== -1 ? email.substring(indexAt) : null;
}

// Função para gerar um hash baseado no timestamp atual
function gerarHashAleatorio() {
  const timestamp = Date.now();
  const randomNum = Math.floor(Math.random() * 1000000);
  return `user_${timestamp.toString(36)}_${randomNum.toString(36)}`;
}

// Função para extrair o nome da empresa do email ou gerar hash para emails públicos
function extrairNomeEmpresa(email) {
  if (!email) return null;
  
  // Extrair o domínio do email
  const dominio = extrairDominio(email);
  if (!dominio) return null;
  
  // Verificar se é um domínio público
  const ehDominioPublico = dominiosPublicos.some(d => 
    dominio.toLowerCase() === d.toLowerCase()
  );
  
  if (ehDominioPublico) {
    // Para emails públicos, gerar um hash aleatório
    return gerarHashAleatorio();
  } else {
    // Para emails corporativos, extrair nome da empresa
    const match = email.match(/@([^.]+)\./);
    return match ? match[1] : null;
  }
}

// Componente de Modal
const SuccessModal = ({ message, onClose }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Cadastro Realizado</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="modal-button" onClick={onClose}>Fazer Login</button>
        </div>
      </div>
    </div>
  );
};

const Signup = ({ onBackToLogin }) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { darkMode, toggleTheme } = useTheme();
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);

  const [senhaValida, setSenhaValida] = useState({
    tamanho: false,
    letra: false,
    numero: false,
    especial: false
  });

  useEffect(() => {
    // Validar senha quando ela mudar
    const senha = password;
    setSenhaValida({
      tamanho: senha.length >= 8,
      letra: /[a-zA-Z]/.test(senha),
      numero: /[0-9]/.test(senha),
      especial: /[!@#$%^&*(),.?":{}|<>]/.test(senha)
    });
  }, [password]);

  const senhaAtendeRequisitos = () => {
    return Object.values(senhaValida).every(valor => valor === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validação dos dados
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (!senhaAtendeRequisitos()) {
      setError('A senha não atende aos requisitos mínimos de segurança.');
      return;
    }

    if (!username.trim()) {
      setError('Nome de usuário é obrigatório.');
      return;
    }

    if (!email.trim()) {
      setError('Email é obrigatório.');
      return;
    }

    // Extrair nome da empresa do email
    const empresa = extrairNomeEmpresa(email);
    if (!empresa) {
      setError('Não foi possível extrair o nome da empresa do email. Use um email corporativo válido.');
      return;
    }

    setLoading(true);

    try {
      const userData = {
        nome: name,
        email: email,
        username: username.toLowerCase(),
        empresa: empresa,
        senha: password,
        role: 'inativo'
      };

      const result = await apiService.signup(userData);
      
      if (result.success) {
        setSuccessMessage(result.message || 'Cadastro realizado com sucesso! Aguarde a ativação da sua conta pelo administrador.');
        setShowModal(true);
        
        // Limpar os campos do formulário
        setName('');
        setEmail('');
        setUsername('');
        setPassword('');
        setConfirmPassword('');
      } else {
        setError(result.error || 'Erro ao criar conta');
      }
    } catch (error) {
      console.error("Erro no cadastro:", error);
      setError('Erro de conexão. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  // Função para fechar o modal e redirecionar para login
  const handleModalClose = () => {
    setShowModal(false);
    // Redirecionar para a página de login
    onBackToLogin();
  };

  // Exibir a empresa que será usada quando o usuário digitar o email
  const empresaDetectada = email ? extrairNomeEmpresa(email) : null;
  const dominio = email ? extrairDominio(email) : null;
  const ehDominioPublico = dominio ? dominiosPublicos.some(d => dominio.toLowerCase() === d.toLowerCase()) : false;

  return (
    <div className={`login-container ${!darkMode ? 'light-mode' : ''}`}>
      {showModal && (
        <SuccessModal
          message={successMessage}
          onClose={handleModalClose}
        />
      )}
      
      <div className="login-card">
        <div className="theme-toggle">
          <button 
            className="theme-button" 
            onClick={toggleTheme}
            aria-label={darkMode ? "Mudar para tema claro" : "Mudar para tema escuro"}
          >
            {darkMode ? <FiSun /> : <FiMoon />}
          </button>
        </div>
        
        <div className="logo-container">
          <img src={kmfLogo} alt="KMF Engenharia" className="logo" />
        </div>
        <h2 className="title">Criar Conta</h2>
        
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label>Nome completo</label>
            <input 
              type="text" 
              placeholder="Digite seu nome completo" 
              value={name}
              onChange={(e) => setName(e.target.value)}
              required 
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label>Nome de usuário</label>
            <input 
              type="text" 
              placeholder="Digite seu nome de usuário" 
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              required 
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label>Email</label>
            <input 
              type="email" 
              placeholder="Digite seu email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required 
              disabled={loading}
            />
            {email && empresaDetectada && ehDominioPublico && (
              <div className="empresa-info email-publico">
                Email pessoal detectado. Um identificador único será criado para sua conta.
              </div>
            )}
            {email && empresaDetectada && !ehDominioPublico && (
              <div className="empresa-info">
                Empresa detectada: <strong>{empresaDetectada}</strong>
              </div>
            )}
            {email && !empresaDetectada && (
              <div className="empresa-warning">
                Formato de email não reconhecido. Verifique se o formato está correto.
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label>Senha</label>
            <div className="senha-input-container">
              <input 
                type={mostrarSenha ? "text" : "password"}
                placeholder="Digite uma senha forte" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required 
                disabled={loading}
                className={password && !senhaAtendeRequisitos() ? "senha-invalida" : ""}
                title="Digite uma senha forte com letras, números e caracteres especiais"
              />
              <button
                type="button"
                className="btn-mostrar-senha"
                onClick={() => setMostrarSenha(!mostrarSenha)}
                title={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
              >
                {mostrarSenha ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
            <div className="senha-requisitos">
              <p className="requisito-titulo">A senha deve conter:</p>
              <ul className="requisitos-lista">
                <li className={senhaValida.tamanho ? "requisito-atendido" : ""}>
                  {senhaValida.tamanho ? "✓" : "○"} Mínimo de 8 caracteres
                </li>
                <li className={senhaValida.letra ? "requisito-atendido" : ""}>
                  {senhaValida.letra ? "✓" : "○"} Pelo menos uma letra
                </li>
                <li className={senhaValida.numero ? "requisito-atendido" : ""}>
                  {senhaValida.numero ? "✓" : "○"} Pelo menos um número
                </li>
                <li className={senhaValida.especial ? "requisito-atendido" : ""}>
                  {senhaValida.especial ? "✓" : "○"} Pelo menos um caractere especial (!@#$%^&*)
                </li>
              </ul>
            </div>
          </div>
          
          <div className="form-group">
            <label>Confirmar Senha</label>
            <div className="senha-input-container">
              <input 
                type={mostrarConfirmarSenha ? "text" : "password"}
                placeholder="Confirme sua senha" 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required 
                disabled={loading}
                className={confirmPassword && password !== confirmPassword ? "senha-invalida" : ""}
              />
              <button
                type="button"
                className="btn-mostrar-senha"
                onClick={() => setMostrarConfirmarSenha(!mostrarConfirmarSenha)}
                title={mostrarConfirmarSenha ? "Ocultar senha" : "Mostrar senha"}
              >
                {mostrarConfirmarSenha ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <small className="error-text">As senhas não coincidem</small>
            )}
          </div>
          
          <button 
            type="submit" 
            className="login-button" 
            disabled={loading || !senhaAtendeRequisitos() || password !== confirmPassword}
          >
            Cadastrar
          </button>
          <button 
            type="button" 
            className="register-button" 
            onClick={onBackToLogin}
            disabled={loading}
          >
            Voltar para Login
          </button>
        </form>
        
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Criando sua conta...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup; 